import {useRef, useState} from 'react';
import {view, Icon, Divider, Menu, MenuItem, Button, Tooltip} from '@sail/ui';
import {assignProps} from '@sail/engine';
import {clipboard, more, external} from '@sail/icons/react/Icon';
import {defineMessages, useIntl} from 'react-intl';

type PaymentLinkCardProps = {
  paymentLink: string;
  paymentLinkId: string;
  productName?: string | undefined | null;
  price?: number | undefined | null;
  currency?: string | undefined | null;
};

const messages = defineMessages({
  cardTitle: {
    id: 'conversations_ui.payment_link_card.title',
    description: 'Payment Link Card title',
    defaultMessage: 'Payment link',
  },
  copyLink: {
    id: 'conversations_ui.payment_link_card.copy_link',
    description: 'Label for the copy link button',
    defaultMessage: 'Copy link',
  },
  copyLinkToast: {
    id: 'conversations_ui.payment_link_card.copy_link_toast',
    description: 'Notification of a successful copy from the copy link button',
    defaultMessage: 'Copied',
  },
});

export const PaymentLinkCard = ({
  paymentLink,
  paymentLinkId,
  productName,
  price,
  currency,
}: PaymentLinkCardProps) => {
  const intl = useIntl();
  const copyLinkRef = useRef<HTMLDivElement>(null);
  const [isCopied, setIsCopied] = useState(false);

  return (
    <view.div
      css={{
        borderRadius: 'medium',
        padding: 'space.150',
        borderColor: 'border',
        borderWidth: 1,
        borderStyle: 'solid',
        stack: 'y',
        gap: 'xxsmall',
        maxWidth: '500px',
        position: 'relative',
      }}
    >
      <view.div css={{stack: 'x', gap: 'small'}}>
        <view.span
          css={{
            color: 'subdued',
            font: 'label.medium.emphasized',
          }}
        >
          {intl.formatMessage(messages.cardTitle)}
        </view.span>
        {productName ? (
          <view.div css={{height: 10, stack: 'x', width: 0}}>
            <Divider />
          </view.div>
        ) : null}
        {productName ? (
          <view.span
            css={{
              color: 'subdued',
              font: 'label.medium',
            }}
          >
            {productName}
          </view.span>
        ) : null}
      </view.div>
      <Menu
        trigger={
          <Button
            css={{
              boxShadow: 'none',
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'subdued',
            }}
          >
            <Icon icon={more} size="small" />
          </Button>
        }
        subviews={{popover: {uses: [assignProps({placement: 'top'})]}}}
      >
        <MenuItem
          href={`https://dashboard.stripe.com/payment-links/${paymentLinkId}`}
        >
          Edit
        </MenuItem>
        <MenuItem href={paymentLink}>
          <view.div css={{display: 'flex', justifyContent: 'space-between'}}>
            Preview <Icon icon={external} size="small" />
          </view.div>
        </MenuItem>
      </Menu>
      {price && currency ? (
        <view.span
          css={{
            font: 'heading.large',
          }}
        >
          {intl.formatNumber(price / 100, {
            style: 'currency',
            currency,
          })}
        </view.span>
      ) : null}
      <Tooltip
        trigger={copyLinkRef}
        placement="top"
        css={{
          padding: 'small',
          color: 'onFeedback.neutral.emphasized',
        }}
      >
        {isCopied ? 'Copied!' : 'Copy link'}
      </Tooltip>
      <view.div
        ref={copyLinkRef}
        onClick={() => {
          navigator.clipboard.writeText(paymentLink);
          setIsCopied(true);
          setTimeout(() => {
            setIsCopied(false);
          }, 2000);
        }}
        css={{
          borderRadius: 'medium',
          padding: 'small',
          borderColor: '#ebeef1',
          borderWidth: 1,
          borderStyle: 'solid',
          marginTop: 'space.75',
          backgroundColor: isCopied ? 'feedback.critical' : 'offset',
          display: 'flex',
          overflow: 'hidden',
          justifyContent: 'space-between',
          alignItems: 'center',
          ':hover': {
            cursor: 'pointer',
          },
        }}
      >
        <view.span
          css={{
            font: 'label.medium.emphasized',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontFamily: 'monospace',
            flexGrow: 1,
            flexShrink: 1,
          }}
        >
          {paymentLink}
        </view.span>
        <Icon
          icon={clipboard}
          size="small"
          css={{
            fill: 'action.primary',
            flexShrink: 0,
            marginLeft: 'space.50',
          }}
        />
      </view.div>
    </view.div>
  );
};
